import React from "react";
import { Link } from "react-router-dom";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Background Pattern */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_dealauth-api/artifacts/z6ik3546_Subtle%20Pattern%20On%20Blue.png)',
        }}
      />
      
      {/* Side Fade Overlays */}
      <div className="fixed inset-0 bg-gradient-to-r from-slate-900/60 via-transparent to-slate-900/60 pointer-events-none" />
      
      {/* Header */}
      <header className="relative z-10 border-b border-white/10 bg-slate-900/30 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-sm text-cyan-400 hover:text-cyan-300">
              ← Back to Deal Room
            </Link>
            <div className="text-sm font-semibold">Jigsaw Conferences Ltd</div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-12">
          <h1 className="text-3xl font-bold mb-6">Jigsaw Deal Room – Privacy Notice</h1>
          
          <div className="space-y-6 text-slate-300">
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">1. Who we are</h2>
              <p>
                This Privacy Notice explains how <strong>Jigsaw Conferences Ltd</strong> (<strong>Jigsaw</strong>, <strong>we</strong>, <strong>us</strong>, <strong>our</strong>) 
                uses personal data in connection with the Jigsaw Deal Room at <strong>dealroom.jigsawconferences.co.uk</strong>.
              </p>
              <p className="mt-3">
                Jigsaw Conferences Ltd is a company registered in England and Wales with company number <strong>04788489</strong> and 
                registered office at <strong>3rd Floor, 45 Albemarle Street, London, W1S 4JL, England</strong>.
              </p>
              <p className="mt-3">
                We are the “data controller” in respect of the personal data processed through the Deal Room.
              </p>
              <p className="mt-3">You can contact us at:</p>
              <p className="mt-2">
                <strong>Email:</strong>{" "}
                <a href="mailto:privacy@jigsawconferences.co.uk" className="text-cyan-400 hover:text-cyan-300 underline">
                  privacy@jigsawconferences.co.uk
                </a>
              </p>
              <p className="mt-1">
                <strong>Postal:</strong> Jigsaw Conferences Ltd, 3rd Floor, 45 Albemarle Street, London, W1S 4JL, England
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">2. What this notice covers</h2>
              <p>This Notice applies to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>Individuals who log into the Deal Room (for example, buyers, investors and their representatives); and</li>
                <li>Referees and other individuals whose information is provided as part of the qualification process.</li>
              </ul>
              <p className="mt-3">
                It does not cover our wider website or services; those are addressed in the relevant privacy notices on our main site.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">3. Personal data we collect</h2>
              <p>Through the Deal Room we may collect and process:</p>
              
              <div className="mt-4 space-y-3">
                <div>
                  <h3 className="font-semibold text-white">Identity and contact details</h3>
                  <p className="text-sm">Name, job title, organisation, business email address, phone number.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-white">Deal information</h3>
                  <p className="text-sm">
                    Your answers in the qualification wizard, including investment thesis, transaction structure, track record and related commentary.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-white">References and third-party details</h3>
                  <p className="text-sm">Names, roles, organisations and contact details of referees or other professional contacts you provide.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-white">Technical and usage data</h3>
                  <p className="text-sm">
                    Login date/time, IP address, browser type, device information, pages visited and actions taken in the Deal Room.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-white">Documentation</h3>
                  <p className="text-sm">
                    Proof of funds, commitment letters, or other documents you upload (which may contain personal data).
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-white">Communications</h3>
                  <p className="text-sm">
                    Emails and messages relating to your use of the Deal Room and any potential transaction.
                  </p>
                </div>
              </div>
              
              <p className="mt-4">
                We do not intentionally seek to collect special category data (such as health information, racial or ethnic origin, or 
                political opinions) through the Deal Room, and you should avoid including such information unless it is strictly necessary 
                and you are permitted to share it.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">4. How we use your personal data and legal bases</h2>
              <p>We use personal data from the Deal Room for the following purposes:</p>
              
              <div className="mt-4 space-y-4">
                <div>
                  <h3 className="font-semibold text-white">Evaluating potential transactions and counterparties</h3>
                  <p className="text-sm">
                    To review your answers, assess your suitability as a potential buyer or investor, and manage our internal decision-making.
                  </p>
                  <p className="text-sm mt-1 italic">
                    <strong>Legal basis:</strong> our legitimate interests in assessing and progressing potential transactions and protecting our business.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-white">Taking steps prior to entering into a contract</h3>
                  <p className="text-sm">
                    To discuss, negotiate and prepare heads of terms or transaction documents with you.
                  </p>
                  <p className="text-sm mt-1 italic">
                    <strong>Legal basis:</strong> performance of a contract and taking steps at your request before entering into a contract.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-white">Risk management, compliance and record-keeping</h3>
                  <p className="text-sm">
                    To maintain an audit trail of Deal Room activity, comply with legal or regulatory requirements, and manage disputes or claims.
                  </p>
                  <p className="text-sm mt-1 italic">
                    <strong>Legal basis:</strong> legal obligations and our legitimate interests in protecting our rights.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-white">Communications</h3>
                  <p className="text-sm">
                    To send confirmation emails, clarification requests, meeting invitations and other messages relating to the potential transaction.
                  </p>
                  <p className="text-sm mt-1 italic">
                    <strong>Legal basis:</strong> legitimate interests in effective communication and process management.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-white">System administration and security</h3>
                  <p className="text-sm">
                    To monitor and maintain the secure operation of the Deal Room, detect and prevent fraud or abuse, and troubleshoot issues.
                  </p>
                  <p className="text-sm mt-1 italic">
                    <strong>Legal basis:</strong> our legitimate interests in ensuring the security and integrity of our systems.
                  </p>
                </div>
              </div>
              
              <p className="mt-4">
                Where you provide referee or third-party details, you confirm that those individuals have been informed that their details 
                will be shared with Jigsaw and used for referencing and due diligence in relation to a potential transaction.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">5. Who we share your data with</h2>
              <p>We may share personal data from the Deal Room with:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>Our internal teams involved in corporate development, management, finance, legal and risk.</li>
                <li>Professional advisers, such as lawyers, accountants and corporate finance advisers, where needed for the transaction.</li>
                <li>Service providers who host or support the Deal Room and related systems, such as:
                  <ul className="list-disc list-inside ml-6 mt-1">
                    <li>Hosting and infrastructure providers (e.g. Emergent platform, database hosting);</li>
                    <li>Email providers (e.g. Microsoft 365);</li>
                    <li>IT and security providers.</li>
                  </ul>
                </li>
              </ul>
              <p className="mt-3">
                These service providers act as our “processors” and only process personal data on our instructions and for the purposes described above.
              </p>
              <p className="mt-3">
                We may also share information where required by law, regulation, court order or a competent authority.
              </p>
              <p className="mt-3">
                <strong>We do not sell your personal data.</strong>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">6. International transfers</h2>
              <p>
                Some of our service providers or their systems may be located outside the UK and European Economic Area (EEA). 
                Where we transfer personal data internationally, we will ensure appropriate safeguards are in place, such as:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>UK/EEA adequacy decisions; or</li>
                <li>Standard contractual clauses or equivalent measures approved by relevant regulators.</li>
              </ul>
              <p className="mt-3">
                You can contact us for more information about international transfers and the safeguards we use.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">7. How long we keep your data</h2>
              <p>
                We keep Deal Room data for as long as reasonably necessary for the purposes described above, including to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>Evaluate and manage potential transactions;</li>
                <li>Maintain an audit trail of the process; and</li>
                <li>Comply with legal, regulatory and accounting obligations.</li>
              </ul>
              <p className="mt-3">
                As a guide, Deal Room records are typically retained for <strong>up to 24 months</strong> from the last material activity, 
                unless a longer period is required in connection with a live or historic transaction, dispute or regulatory requirement.
              </p>
              <p className="mt-3">
                We may anonymise data for statistical or analytical purposes, in which case it will no longer be treated as personal data.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">8. Your rights</h2>
              <p>Under UK data protection law, you have certain rights in relation to your personal data, including:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li><strong>Right of access</strong> – to obtain a copy of the personal data we hold about you.</li>
                <li><strong>Right to rectification</strong> – to correct inaccurate or incomplete data.</li>
                <li><strong>Right to erasure</strong> – to request deletion of your data in certain circumstances.</li>
                <li><strong>Right to restriction</strong> – to request that we limit how we process your data in certain cases.</li>
                <li><strong>Right to object</strong> – to object to processing based on our legitimate interests, on grounds relating to your particular situation.</li>
                <li><strong>Right to data portability</strong> – in some circumstances, to receive data you have provided to us in a structured, commonly used format.</li>
              </ul>
              <p className="mt-3">
                These rights are subject to legal conditions and exemptions. If you exercise your rights, we may need to verify your identity 
                before acting on your request.
              </p>
              <p className="mt-3">
                To exercise any of these rights, please contact us at{" "}
                <a href="mailto:privacy@jigsawconferences.co.uk" className="text-cyan-400 hover:text-cyan-300 underline">
                  privacy@jigsawconferences.co.uk
                </a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">9. Complaints</h2>
              <p>
                If you have concerns about how we handle your personal data, we encourage you to contact us first so we can try to resolve the issue.
              </p>
              <p className="mt-3">
                You also have the right to lodge a complaint with the UK Information Commissioner’s Office (ICO):
              </p>
              <p className="mt-2">
                <strong>Website:</strong>{" "}
                <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">
                  ico.org.uk
                </a>
              </p>
              <p className="mt-1">
                <strong>Telephone (UK):</strong> 0303 123 1113
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">10. Updates to this notice</h2>
              <p>
                We may update this Privacy Notice from time to time to reflect changes in our practices, technology or legal requirements. 
                The updated version will be posted in the Deal Room with a revised “last updated” date.
              </p>
            </section>

            <section className="text-sm text-slate-400 mt-8 pt-6 border-t border-white/10">
              <p><strong>Last updated:</strong> January 2025</p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-6 text-center text-xs text-slate-400">
        <p>© Jigsaw Conferences Ltd. All rights reserved.</p>
        <div className="mt-2 space-x-2">
          <Link to="/legal/terms" className="hover:text-white">Terms</Link>
          <span>|</span>
          <Link to="/legal/privacy" className="hover:text-white">Privacy</Link>
          <span>|</span>
          <Link to="/legal/cookies" className="hover:text-white">Cookies</Link>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
